import { apiClient } from '../apiClient';

export interface Group {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface GroupMembership extends Group {
  role: 'admin' | 'member';
  status: 'pending' | 'approved' | 'rejected';
  joinedAt: string;
}

export interface GroupMember {
  userId: string;
  role: 'admin' | 'member';
  status: 'pending' | 'approved' | 'rejected';
  joinedAt: string;
  email: string;
  firstName?: string;
  lastName?: string;
  profileImageUrl?: string;
}

export interface PendingRequest {
  groupId: string;
  groupName: string;
  userId: string;
  userEmail: string;
  userFirstName?: string;
  userLastName?: string;
  requestedAt: string;
}

// Get all groups
export async function getAllGroups(): Promise<Group[]> {
  const response = await apiClient.get('/api/groups');
  return response.data.groups;
}

// Get user's groups
export async function getMyGroups(): Promise<GroupMembership[]> {
  const response = await apiClient.get('/api/groups/my-groups');
  return response.data.groups;
}

// Get a specific group
export async function getGroup(id: string): Promise<Group> {
  const response = await apiClient.get(`/api/groups/${id}`);
  return response.data.group;
}

// Create a new group
export async function createGroup(data: { name: string; description?: string }): Promise<Group> {
  const response = await apiClient.post('/api/groups', data);
  return response.data.group;
}

// Update a group
export async function updateGroup(id: string, data: { name?: string; description?: string }): Promise<Group> {
  const response = await apiClient.put(`/api/groups/${id}`, data);
  return response.data.group;
}

// Delete a group
export async function deleteGroup(id: string): Promise<void> {
  await apiClient.delete(`/api/groups/${id}`);
}

// Get group members
export async function getGroupMembers(groupId: string): Promise<GroupMember[]> {
  const response = await apiClient.get(`/api/groups/${groupId}/members`);
  return response.data.members;
}

// Request to join a group
export async function joinGroup(groupId: string): Promise<void> {
  await apiClient.post(`/api/groups/${groupId}/join`);
}

// Approve or reject a member
export async function updateMemberStatus(
  groupId: string,
  userId: string,
  status: 'approved' | 'rejected',
  role?: 'admin' | 'member'
): Promise<void> {
  await apiClient.patch(`/api/groups/${groupId}/members/${userId}`, { status, role });
}

// Remove a member from a group
export async function removeMember(groupId: string, userId: string): Promise<void> {
  await apiClient.delete(`/api/groups/${groupId}/members/${userId}`);
}

// Get pending join requests for groups where user is admin
export async function getPendingRequests(): Promise<PendingRequest[]> {
  const response = await apiClient.get('/api/groups/admin/pending-requests');
  return response.data.requests;
}
